import { defaultConfig } from '../lib/default-ui-config';
import { UIConfig } from '../types/ui-config';
import { DesignTokens } from '../types/ui-config/design-tokens';
import {
  getBackgroundColorsOld,
  getBrandColors,
  isValidHexColor,
} from './color';

import { isObject } from 'lodash';

function customDeepMerge(target, source) {
  Object.keys(source).forEach((key) => {
    const targetValue = target[key];
    const sourceValue = source[key];

    if (Array.isArray(sourceValue)) {
      // Merge arrays by replacing target array with source array
      target[key] = sourceValue;
    } else if (isObject(sourceValue)) {
      // If target[key] is not an object, replace it with an empty object
      if (!isObject(targetValue)) {
        target[key] = {};
      }
      // Recursively merge objects
      customDeepMerge(target[key], sourceValue);
    } else {
      // For primitive values, just assign the source value to the target
      target[key] = sourceValue;
    }
  });

  return target;
}

export const extendedMerge = (config, baseConfig) => {
  let newConfig = Object.assign({}, baseConfig);

  customDeepMerge(newConfig, config);
  return newConfig;
};

/**
 * Extend the default UI Config with your own
 * @param config Your extended UI Config
 * @returns New extended UI Config object
 */
export const extendConfig = (
  config: UIConfig,
  baseConfig: UIConfig = defaultConfig
) => {
  let newConfig = Object.assign({}, baseConfig);

  customDeepMerge(newConfig, config);
  return newConfig;
};

type ConfigData = {
  showSetupScreen: boolean;
};

type ConfigOptions = {
  grid_pagination?: boolean;
  settings_toggle?: boolean;
};

/**
 * Generates a config with older theme value.
 * @param oldConfig V1 Theme object
 * @param toExtend UI Config object to extend the generated config
 * @param options Options for toggling components
 * @returns
 */
export const generateConfig = (
  oldConfig: any,
  toExtend: UIConfig = {},
  options: ConfigOptions = { grid_pagination: true, settings_toggle: true }
): { config: UIConfig; data: ConfigData } => {
  const data: ConfigData = { showSetupScreen: true };

  let logo: string;

  let meetingElements = ['dyte-stage'];

  let headerChildren = {},
    controlBarChildren = {};

  const showSettingsToggle = options?.settings_toggle !== false;
  const showGridPagination = options?.grid_pagination !== false;

  if (oldConfig.controlBar?.isEnabled) {
    meetingElements.push('dyte-controlbar');

    const { elements } = oldConfig.controlBar;

    const leftElements = ['dyte-breakout-rooms-toggle'],
      rightElements = [];

    const centerElements = [
      'dyte-mic-toggle',
      'dyte-camera-toggle',
      'dyte-webinar-stage-toggle',
      'dyte-mute-all-button',
      ...(showSettingsToggle ? ['dyte-settings-toggle'] : []),
      'dyte-leave-button',
    ];

    if (elements.fullscreen) {
      leftElements.push('dyte-fullscreen-toggle');
    }

    if (elements.screenshare) {
      leftElements.push('dyte-screen-share-toggle');
    }

    // NOTE(callmetarush): No condition as permission check happens
    // within component
    leftElements.push('dyte-recording-toggle');

    // NOTE(madhugb): No condition as permission check happens
    // within component
    leftElements.push('dyte-pip-toggle');

    if (elements.chat) {
      rightElements.push('dyte-chat-toggle');
    }

    if (elements.polls) {
      rightElements.push('dyte-polls-toggle');
    }

    if (elements.participants) {
      rightElements.push('dyte-participants-toggle');
    }

    if (elements.plugins) {
      rightElements.push('dyte-plugins-toggle');
    }

    const allSideElements = leftElements
      .concat(['dyte-mute-all-button'])
      .concat(rightElements);

    if (showSettingsToggle) {
      allSideElements.push('dyte-settings-toggle');
    }

    let hasMobileDrawer = false;

    if (allSideElements.length > 0) {
      hasMobileDrawer = true;
    }

    controlBarChildren = {
      'dyte-controlbar': {
        states: ['activeMoreMenu'],
        children: [
          'div#controlbar-left',
          'div#controlbar-center',
          'div#controlbar-right',
        ],
      },

      'div#controlbar-left': leftElements,
      'div#controlbar-center': centerElements,
      'div#controlbar-right': rightElements,

      'dyte-controlbar.sm': ['div#controlbar-mobile'],
      'dyte-controlbar.md': ['div#controlbar-mobile'],

      'dyte-controlbar.activeMoreMenu.sm': [
        'div#controlbar-mobile',
        'div#more-menu',
      ],
      'dyte-controlbar.activeMoreMenu.md': [
        'div#controlbar-mobile',
        'div#more-menu',
      ],

      'div#controlbar-mobile': [
        'dyte-mic-toggle',
        'dyte-camera-toggle',
        'dyte-webinar-stage-toggle',
        'dyte-leave-button',
        ...[hasMobileDrawer && 'dyte-more-toggle'],
      ],

      'div#more-menu': allSideElements.map((el) => [
        el,
        { variant: 'horizontal' },
      ]),
    };
  }

  if (oldConfig.header?.isEnabled) {
    meetingElements.unshift('dyte-header');

    const { elements } = oldConfig.header;

    let leftElements = ['dyte-recording-indicator'],
      centerElements = [],
      rightElements = [];

    if (showGridPagination) {
      rightElements.push('dyte-grid-pagination');
    }

    if (elements.title) {
      centerElements.push('dyte-meeting-title');
    }

    if (typeof elements.logo === 'string' && elements.logo.length > 0) {
      logo = elements.logo;
      leftElements.unshift('dyte-logo');
    }

    if (elements.participantCount) {
      rightElements.push('dyte-participant-count');
    }

    if (elements.timer) {
      rightElements.push('dyte-clock');
    }

    headerChildren = {
      'dyte-header': [
        'div#header-left',
        'div#header-center',
        'div#header-right',
      ],
      'dyte-header.sm': ['div#header-left', 'div#header-right'],

      'div#header-left': leftElements,
      'div#header-center': centerElements,
      'div#header-right': rightElements,

      // do not use dyte-logo in sm breakpoint
      'div#header-left.sm': leftElements
        .slice(1)
        .concat(['dyte-meeting-title']),
    };
  }

  meetingElements.push('dyte-participants-audio', 'dyte-dialog-manager');

  const designTokens: DesignTokens = {
    logo,
  };

  if (oldConfig.colors != null) {
    designTokens.colors = {};

    if (isValidHexColor(oldConfig.colors.primary)) {
      designTokens.colors.brand = getBrandColors(oldConfig.colors.primary);
    } else if (typeof oldConfig.colors.primary === 'object') {
      designTokens.colors.brand = oldConfig.colors.primary;
    }
    if (isValidHexColor(oldConfig.colors.background)) {
      designTokens.colors.background = getBackgroundColorsOld(
        oldConfig.colors.background
      );
    } else if (typeof oldConfig.colors.background === 'object') {
      designTokens.colors.background = oldConfig.colors.background;
    }
    if (isValidHexColor(oldConfig.colors.text)) {
      designTokens.colors.textOnBrand = oldConfig.colors.text;
    }
    if (isValidHexColor(oldConfig.colors.text)) {
      designTokens.colors.textOnBrand = oldConfig.colors.text;
    }
    if (isValidHexColor(oldConfig.colors.videoBackground)) {
      designTokens.colors.videoBg = oldConfig.colors.videoBackground;
    }
  }

  let config: UIConfig = {
    designTokens,
    styles: {
      'dyte-header': {
        display: 'grid',
        height: '48px',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
      },
      'dyte-header.sm': {
        display: 'grid',
        gridArea: 'header',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
      },
      'div#header-left': {
        display: 'flex',
        alignItems: 'center',
        height: '48px',
      },
      'dyte-logo': {
        height: '26px',
      },
      'div#header-center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      'div#header-right': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      'dyte-stage': {
        display: 'flex',
        flex: '1',
      },
      'dyte-grid': {
        flex: '1',
        height: 'auto',
      },
      'dyte-controlbar': {
        display: 'grid',
        height: '76px',
        gridTemplateColumns: 'repeat(3,1fr)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
        padding: '8px',
      },
      'dyte-controlbar.sm': {
        display: 'flex',
        position: 'relative',
        backgroundColor: 'var(--dyte-colors-background-1000, #000)',
      },
      'dyte-controlbar.md': {
        display: 'flex',
        position: 'relative',
        backgroundColor: 'var(--dyte-colors-background-1000, #000)',
      },
      'div#controlbar-left': {
        display: 'flex',
        alignItems: 'center',
      },
      'div#controlbar-center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      'div#controlbar-mobile': {
        display: 'flex',
        flex: '1',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '10000',
      },
      'div#controlbar-right': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      'dyte-settings': {
        width: '720px',
        height: '480px',
      },
      'div#more-menu': {
        flex: 1,
        position: 'absolute',
        boxSizing: 'border-box',
        top: '0',
        left: '0',
        width: '100%',
        transform: 'translateY(-100%)',
        backgroundColor: 'rgb(var(--dyte-colors-background-1000, 0 0 0))',
        padding: '16px 12px',
        borderRadius: '18px 18px 0 0',
        zIndex: '10000',
      },
      'div#setupcontrols-media': {
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        display: 'flex',
      },
      'div#setupcontrols-settings': {
        position: 'absolute',
        top: '8px',
        right: '8px',
      },
      'dyte-meeting-title.sm': {
        marginLeft: '0',
      },
      'dyte-clock': {
        marginRight: '0',
      },
    },
    root: {
      'dyte-meeting': {
        // if using key value pair, provide the key in `state`
        // else provide array of states in `states`
        state: 'meeting',
        states: ['activeSidebar'],
      },
      'dyte-meeting[meeting=idle]': ['dyte-idle-screen'],
      'dyte-meeting[meeting=waiting]': ['dyte-waiting-screen'],
      'dyte-meeting[meeting=setup]': [
        'dyte-setup-screen',
        'dyte-dialog-manager',
      ],
      'dyte-meeting[meeting=joined]': meetingElements,
      'dyte-meeting[meeting=joined].activeSidebar.sm': [
        ...meetingElements,
        ['dyte-sidebar', { view: 'full-screen' }],
      ],
      'dyte-meeting[meeting=joined].activeSidebar.md': [
        ...meetingElements,
        ['dyte-sidebar', { view: 'full-screen' }],
      ],
      'dyte-meeting[meeting=ended]': ['dyte-ended-screen'],

      ...headerChildren,
      ...controlBarChildren,

      'dyte-stage': {
        states: ['activeSidebar', 'activeBreakoutRoomGrid'],
        children: ['dyte-grid', 'dyte-notifications'],
      },

      'dyte-stage.activeBreakoutRoomGrid': [
        'dyte-breakout-room-grid',
        'dyte-notifications',
        'dyte-sidebar',
      ],

      'dyte-stage.activeBreakoutRoomGrid.activeSidebar': [
        'dyte-breakout-room-grid',
        'dyte-notifications',
        ['dyte-sidebar', { view: 'sidebar' }],
      ],

      'dyte-stage.activeSidebar': [
        'dyte-grid',
        'dyte-notifications',
        ['dyte-sidebar', { view: 'sidebar' }],
      ],

      // hide sidebar for smaller screens
      'dyte-stage.activeSidebar.sm': ['dyte-grid', 'dyte-notifications'],

      'dyte-grid': {
        states: ['activeScreenShare', 'activePlugin', 'activeSpotlight'],
        children: ['dyte-simple-grid'],
      },

      'dyte-grid.activeSpotlight': ['dyte-spotlight-grid'],

      'dyte-grid.activeScreenShare': ['dyte-mixed-grid'],
      'dyte-grid.activePlugin': ['dyte-mixed-grid'],

      'dyte-grid.activeScreenShare.activeSpotlight': ['dyte-mixed-grid'],
      'dyte-grid.activePlugin.activeSpotlight': ['dyte-mixed-grid'],
      'dyte-grid.activePlugin.activeScreenShare.activeSpotlight': [
        'dyte-mixed-grid',
      ],

      'dyte-mixed-grid': {
        states: ['activeSpotlight'],
        children: ['dyte-simple-grid'],
      },

      'dyte-mixed-grid.activeSpotlight': ['dyte-spotlight-grid'],

      'dyte-participant-tile': {
        state: 'meeting',
        children: ['dyte-name-tag', 'dyte-avatar'],
      },

      'dyte-participant-setup': {
        children: ['dyte-avatar', 'div#setupcontrols-media'],
      },

      'dyte-participant-tile[meeting=setup]': {
        children: [
          'dyte-avatar',
          'div#setupcontrols-media',
          ...(showSettingsToggle ? ['div#setupcontrols-settings'] : []),
        ],
      },

      'div#setupcontrols-media': [
        ['dyte-mic-toggle', { size: 'sm' }],
        ['dyte-camera-toggle', { size: 'sm' }],
      ],
      'div#setupcontrols-settings': [['dyte-settings-toggle', { size: 'sm' }]],

      'dyte-screenshare-view': {
        children: ['dyte-name-tag'],
      },

      'dyte-name-tag': [['dyte-audio-visualizer', { slot: 'start' }]],
    },
    config: {
      notifications: ['chat', 'participant_joined', 'participant_left'],
      notification_sounds: ['chat', 'participant_joined'],
      videoFit: 'cover',
    },
  };

  config = extendConfig(toExtend, config);

  data.showSetupScreen = oldConfig.setupScreen?.isEnabled ?? true;

  return { config, data };
};
